import { Directive, ElementRef, EventEmitter, HostListener, Input, Output, OnDestroy } from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MentionListComponent } from './mention-list.component';
import { fromEvent, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

interface MentionConfig {
  items: any[];
  triggerChar: string;
  labelKey: string;
  disableSort?: boolean;
  mentionSelect?: (item: any) => string;
}

@Directive({
  selector: '[appMention]'
})
export class MentionDirective implements OnDestroy {
  @Input() mentionConfig: MentionConfig[] = [];
  @Output() mentionSelected = new EventEmitter<any>();

  private overlayRef: any;
  private mentionList: MentionListComponent;
  private currentTriggerChar: string;
  private currentConfig: MentionConfig;
  private documentClickSubscription: Subscription;

  constructor(
    private elementRef: ElementRef<HTMLTextAreaElement>,
    private overlay: Overlay
  ) {}

  @HostListener('input', ['$event'])
  onInput(event: any) {
    const textarea = this.elementRef.nativeElement;
    const caretPosition = textarea.selectionStart;
    const textBeforeCaret = textarea.value.slice(0, caretPosition);
    const lastChar = textBeforeCaret.slice(-1);
    
    // Check if the last character is any of our trigger characters
    const config = this.mentionConfig.find(c => c.triggerChar === lastChar);
    if (config) {
      this.currentTriggerChar = config.triggerChar;
      this.currentConfig = config;
      this.showMentionList('', config);
    } else if (this.overlayRef) {
      // Find the last trigger character in the text
      const lastTriggerIndex = Math.max(
        ...this.mentionConfig.map(c => textBeforeCaret.lastIndexOf(c.triggerChar))
      );
      
      if (lastTriggerIndex >= 0) {
        const searchText = textBeforeCaret.slice(lastTriggerIndex + 1);
        const triggerChar = textBeforeCaret[lastTriggerIndex];
        const config = this.mentionConfig.find(c => c.triggerChar === triggerChar);
        
        if (config && !searchText.includes(' ')) {
          this.currentTriggerChar = triggerChar;
          this.currentConfig = config;
          this.updateFilteredItems(searchText);
        } else {
          this.closeMentionList();
        }
      } else {
        this.closeMentionList();
      }
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.mentionList) return;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.mentionList.moveSelection('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.mentionList.moveSelection('down');
        break;
      case 'Enter':
        event.preventDefault();
        if (this.mentionList.activeItem) {
          this.mentionList.selectItem(this.mentionList.activeItem);
        }
        break;
      case 'Escape':
        this.closeMentionList();
        break;
      case 'Tab':
        if (this.overlayRef) {
          event.preventDefault();
          if (this.mentionList.activeItem) {
            this.mentionList.selectItem(this.mentionList.activeItem);
          }
        }
        break;
    }
  }

  private showMentionList(searchText: string, config: MentionConfig) {
    if (!this.overlayRef) {
      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.elementRef)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: 8
          }
        ]);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        width: '250px',
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop'
      });

      const portal = new ComponentPortal(MentionListComponent);
      const componentRef = this.overlayRef.attach(portal);
      this.mentionList = componentRef.instance;

      this.mentionList.items = config.items;
      this.mentionList.labelKey = config.labelKey;
      this.mentionList.triggerChar = config.triggerChar;
      
      // Set initial active item
      if (config.items.length > 0) {
        this.mentionList.activeItem = config.items[0];
      }
      
      this.mentionList.itemSelected.subscribe((item: any) => {
        const textarea = this.elementRef.nativeElement;
        const caretPosition = textarea.selectionStart;
        const textBeforeCaret = textarea.value.slice(0, caretPosition);
        const lastTriggerCharIndex = textBeforeCaret.lastIndexOf(this.currentTriggerChar);
        
        const prefix = textarea.value.slice(0, lastTriggerCharIndex);
        const suffix = textarea.value.slice(caretPosition);
        
        let insertText;
        if (this.currentConfig.mentionSelect) {
          insertText = this.currentConfig.mentionSelect(item);
        } else {
          insertText = this.currentTriggerChar + item[this.currentConfig.labelKey] + '}';
        }
        
        textarea.value = prefix + insertText + suffix;
        
        // Set cursor position after the inserted mention
        const newPosition = lastTriggerCharIndex + insertText.length;
        textarea.setSelectionRange(newPosition, newPosition);
        
        // Trigger change event
        const event = new Event('input', { bubbles: true });
        textarea.dispatchEvent(event);
        
        this.mentionSelected.emit(item);
        this.closeMentionList();
      });

      // Handle click outside
      this.documentClickSubscription = fromEvent<MouseEvent>(document, 'click')
        .pipe(
          filter(event => {
            const clickTarget = event.target as HTMLElement;
            // Check if click is outside both the textarea and the mention list
            const clickedInside = this.elementRef.nativeElement.contains(clickTarget) ||
              (this.mentionList && this.mentionList.container && this.mentionList.container.nativeElement.contains(clickTarget));
            return !clickedInside;
          })
        )
        .subscribe(() => {
          this.closeMentionList();
        });
    }

    this.updateFilteredItems(searchText);
  }

  private updateFilteredItems(searchText: string) {
    if (this.mentionList) {
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const items = this.currentConfig.items;
  
        const exactMatches = items.filter(item => 
          item[this.currentConfig.labelKey].toLowerCase() === searchLower
        );
  
        const startMatches = items.filter(item => 
          !exactMatches.includes(item) && 
          item[this.currentConfig.labelKey].toLowerCase().startsWith(searchLower)
        );

        const containsMatches = items.filter(item => 
          !exactMatches.includes(item) && 
          !startMatches.includes(item) &&
          item[this.currentConfig.labelKey].toLowerCase().includes(searchLower)
        );
  
        this.mentionList.filteredItems = [
          ...exactMatches,
          ...startMatches.sort((a, b) => a[this.currentConfig.labelKey].length - b[this.currentConfig.labelKey].length),
          ...containsMatches.sort((a, b) => a[this.currentConfig.labelKey].length - b[this.currentConfig.labelKey].length)
        ];
      } else {
        this.mentionList.filteredItems = [...this.currentConfig.items];
      }
  
      if (this.mentionList.filteredItems.length > 0) {
        this.mentionList.activeItem = this.mentionList.filteredItems[0];
      }
    }
  }

  private closeMentionList() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.mentionList = null;
    }
    if (this.documentClickSubscription) {
      this.documentClickSubscription.unsubscribe();
      this.documentClickSubscription = null;
    }
  }

  ngOnDestroy() {
    this.closeMentionList();
  }
} 